import React from 'react';
import ProfileEditor from '../../../components/ProfileEditor';
import { StorefrontProfile } from '../types';
import { UserProfile, FinancialConfig, Material } from '../../../types';

export interface FixedCostItem {
  id: string;
  name: string;
  monthlyValue: number;
}

export interface ProfileFinancialState {
  desiredProLabore: number;
  workHoursPerDay: number;
  workDaysPerWeek: number;
  vacationWeeksPerYear: number;
  fixedCosts: FixedCostItem[];
  minMarginPercent: number;
}

export interface ProfileEditorModalProps {
  profile: StorefrontProfile;
  financialState: ProfileFinancialState;
  onSaveProfile: (profile: StorefrontProfile, financialState: ProfileFinancialState) => Promise<void> | void;
  onBack: () => void;
}

/**
 * Wrapper de Demonstração / Lab-ERP para Edição de Perfil.
 * Renderiza o componente canônico ProfileEditor com tipagem estrita via React.ComponentProps.
 */
export default function ProfileEditorModal({
  profile,
  financialState,
  onSaveProfile,
  onBack
}: ProfileEditorModalProps) {
  // Trava de tipagem estrita com o componente real de produção
  type EnforcedProfileEditorProps = React.ComponentProps<typeof ProfileEditor>;

  const mockUser: UserProfile = {
    id: profile.userId || 'demo-user-profile',
    ownerName: profile.displayName || 'Gestor Demonstração',
    businessName: profile.displayName || 'Minha Empresa Modelo',
    email: profile.email || 'contato@empresa.com.br',
    phone: profile.whatsappNumber || '(11) 99999-9999',
    businessAddress: profile.addressDisplay || 'Rua Modelo, 123 - Centro',
    niche: 'geral',
    plan: 'Empresa',
    isTrialActive: true,
    maxFreeQuotes: 99999,
  };

  const mockConfig: FinancialConfig = {
    userId: profile.userId || 'demo-user-profile',
    monthlyFixedCost: (financialState.fixedCosts || []).reduce((acc, c) => acc + (c.monthlyValue || 0), 0) || 3500,
    proLaboreGoal: financialState.desiredProLabore || 5000,
    weeklyWorkHours: (financialState.workHoursPerDay || 8) * (financialState.workDaysPerWeek || 5),
    baseHourValue: 65,
    paymentFeePercentage: 3.5,
    defaultCommissionPercentage: 0,
  };

  const handleSave = async (user: UserProfile, config: FinancialConfig) => {
    if (onSaveProfile) {
      await onSaveProfile(
        {
          ...profile,
          displayName: user.businessName || user.ownerName || profile.displayName,
          email: user.email,
          whatsappNumber: user.phone || profile.whatsappNumber,
          addressDisplay: user.businessAddress,
        },
        {
          ...financialState,
          desiredProLabore: config.proLaboreGoal,
        }
      );
    }
    onBack();
  };

  const realProps: EnforcedProfileEditorProps = {
    user: mockUser,
    config: mockConfig,
    materials: [] as Material[],
    onSave: handleSave,
    onClose: onBack,
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm overflow-y-auto">
      <ProfileEditor {...realProps} />
    </div>
  );
}
